<template>
  <form class="form-horizontal">
    <div
      v-bind:class="{ 'has-error': $v.bus[BUS_I].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="bus-i">Bus Number</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <input
            v-model.number="bus[BUS_I]"
            class="form-input"
            id="bus-i"
            inputmode="numeric"
          />
          <select v-model.number="bus[BUS_TYPE]" class="form-select">
            <option value="1">PQ</option>
            <option value="2">PV</option>
            <option value="3">Reference</option>
            <option value="4">Isolated</option>
          </select>
        </div>
        <p v-if="!$v.bus[BUS_I].required" class="form-input-hint">
          Bus number is required.
        </p>
        <p v-else-if="!$v.bus[BUS_I].integer" class="form-input-hint">
          Bus number must be an integer.
        </p>
        <p v-else-if="!$v.bus[BUS_I].minValue" class="form-input-hint">
          Bus number must be >= 1.
        </p>
        <p v-else-if="!$v.bus[BUS_I].unique" class="form-input-hint">
          Bus number must be unique.
        </p>
      </div>
    </div>

    <div v-bind:class="{ 'has-error': $v.bus[VM].$invalid }" class="form-group">
      <div class="col-4 col-sm-12">
        <label class="form-label" for="vm">Voltage Magnitude</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <span class="input-group-addon">V<sub>m</sub></span>
          <input
            v-model.number="bus[VM]"
            class="form-input"
            id="vm"
            inputmode="decimal"
          />
          <span class="input-group-addon">pu</span>
        </div>
        <p v-if="!$v.bus[VM].required" class="form-input-hint">
          Voltage magnitude is required.
        </p>
        <p v-else-if="!$v.bus[VM].decimal" class="form-input-hint">
          Voltage magnitude must be a decimal.
        </p>
        <p v-else-if="!$v.bus[VM].minValue" class="form-input-hint">
          Voltage magnitude must be >= 0.
        </p>
        <p v-else-if="actualVoltage(bus)" class="form-input-hint">
          Actual voltage = {{ actualVoltage(bus) }} kV
        </p>
      </div>
    </div>

    <div v-bind:class="{ 'has-error': $v.bus[VA].$invalid }" class="form-group">
      <div class="col-4 col-sm-12">
        <label class="form-label" for="va">Voltage Angle</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <span class="input-group-addon">V<sub>a</sub></span>
          <input
            v-model.number="bus[VA]"
            class="form-input"
            id="va"
            inputmode="decimal"
          />
          <span class="input-group-addon">&deg;</span>
        </div>
        <p v-if="!$v.bus[VA].required" class="form-input-hint">
          Voltage angle is required.
        </p>
        <p v-else-if="!$v.bus[VA].decimal" class="form-input-hint">
          Voltage angle must be a decimal.
        </p>
        <p v-else-if="!$v.bus[VA].between" class="form-input-hint">
          Voltage angle must be between -360 and 360.
        </p>
        <p v-else-if="radians" class="form-input-hint">{{ radians }} radians</p>
      </div>
    </div>

    <div
      v-bind:class="{ 'has-error': $v.bus[BASE_KV].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="base-kv">Base Voltage</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <input
            v-model.number="bus[BASE_KV]"
            class="form-input"
            id="base-kv"
            inputmode="decimal"
          />
          <span class="input-group-addon">kV</span>
        </div>
        <p v-if="!$v.bus[BASE_KV].required" class="form-input-hint">
          Base voltage is required.
        </p>
        <p v-else-if="!$v.bus[BASE_KV].decimal" class="form-input-hint">
          Base voltage must be a decimal.
        </p>
        <p v-else-if="!$v.bus[BASE_KV].minValue" class="form-input-hint">
          Base voltage must be >= 0.
        </p>
      </div>
    </div>

    <fieldset>
      <h6>
        Load
        <HelpButton section="loads" kind="small" />
      </h6>

      <div
        v-bind:class="{ 'has-error': $v.bus[PD].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="pd">Active Power</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">P<sub>d</sub></span>
            <input
              v-model.number="bus[PD]"
              class="form-input"
              id="pd"
              inputmode="decimal"
            />
            <span class="input-group-addon">MW</span>
          </div>
          <p v-if="!$v.bus[PD].required" class="form-input-hint">
            Active power demand is required.
          </p>
          <p v-else-if="!$v.bus[PD].decimal" class="form-input-hint">
            Active power demand must be a decimal.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.bus[QD].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qd">Reactive Power</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>d</sub></span>
            <input
              v-model.number="bus[QD]"
              class="form-input"
              id="qd"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.bus[QD].required" class="form-input-hint">
            Reactive power demand is required.
          </p>
          <p v-else-if="!$v.bus[QD].decimal" class="form-input-hint">
            Reactive power demand must be a decimal.
          </p>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <h6>
        Shunt
        <HelpButton section="shuntElements" kind="small" />
      </h6>

      <div
        v-bind:class="{ 'has-error': $v.bus[GS].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="gs">Conductance</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">G<sub>s</sub></span>
            <input
              v-model.number="bus[GS]"
              class="form-input"
              id="gs"
              inputmode="decimal"
            />
            <span class="input-group-addon">MW</span>
          </div>
          <p v-if="!$v.bus[GS].required" class="form-input-hint">
            Shunt conductance is required.
          </p>
          <p v-else-if="!$v.bus[GS].decimal" class="form-input-hint">
            Shunt conductance must be a decimal.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.bus[BS].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="bs">Susceptance</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">B<sub>s</sub></span>
            <input
              v-model.number="bus[BS]"
              class="form-input"
              id="bs"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.bus[BS].required" class="form-input-hint">
            Shunt susceptance is required.
          </p>
          <p v-else-if="!$v.bus[BS].decimal" class="form-input-hint">
            Shunt susceptance must be a decimal.
          </p>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <h6>Voltage Limits</h6>

      <div
        v-bind:class="{ 'has-error': $v.bus[VMAX].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="vmax">Maximum Voltage</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">V<sub>max</sub></span>
            <input
              v-model.number="bus[VMAX]"
              class="form-input"
              id="vmax"
              inputmode="decimal"
            />
            <span class="input-group-addon">pu</span>
          </div>
          <p v-if="!$v.bus[VMAX].required" class="form-input-hint">
            Maximum voltage is required.
          </p>
          <p v-else-if="!$v.bus[VMAX].decimal" class="form-input-hint">
            Maximum voltage must be a decimal.
          </p>
          <p v-else-if="!$v.bus[VMAX].minValue" class="form-input-hint">
            Maximum voltage must be >= {{ bus[VMIN] }}.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.bus[VMIN].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="vmin">Minimum Voltage</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">V<sub>min</sub></span>
            <input
              v-model.number="bus[VMIN]"
              class="form-input"
              id="vmin"
              inputmode="decimal"
            />
            <span class="input-group-addon">pu</span>
          </div>
          <p v-if="!$v.bus[VMIN].required" class="form-input-hint">
            Minimum voltage is required.
          </p>
          <p v-else-if="!$v.bus[VMIN].decimal" class="form-input-hint">
            Minimum voltage must be a decimal.
          </p>
          <p v-else-if="!$v.bus[VMIN].maxValue" class="form-input-hint">
            Minimum voltage must be &lt;= {{ bus[VMAX] }}.
          </p>
        </div>
      </div>
    </fieldset>
  </form>
</template>

<script>
import HelpButton from "../HelpButton";

import numeral from "numeral-es6";

import {
  required,
  between,
  maxValue,
  minValue,
  integer,
  decimal
} from "vuelidate/lib/validators";

import MPCStore from "../../stores/MPCStore";

import {
  BUS_I,
  BUS_TYPE,
  PD,
  QD,
  GS,
  BS,
  VM,
  VA,
  BASE_KV,
  VMAX,
  VMIN
} from "../../stores/idx";

export default {
  name: "BusForm",
  props: ["ord"],
  components: { HelpButton },
  data() {
    return {
      bus: MPCStore.methods.getBus(+this.ord - 1),
      BUS_I: BUS_I,
      BUS_TYPE: BUS_TYPE,
      PD: PD,
      QD: QD,
      GS: GS,
      BS: BS,
      VM: VM,
      VA: VA,
      BASE_KV: BASE_KV,
      VMAX: VMAX,
      VMIN: VMIN
    };
  },
  computed: {
    radians: function() {
      if (this.bus[VA] === 0) {
        return "";
      }
      let n = numeral(this.bus[VA] * (Math.PI / 180));
      return n.format("0.[0000]");
    }
  },
  methods: {
    actualVoltage: function(bus) {
      if (bus[BASE_KV] !== 0) {
        let n = numeral(bus[VM] * bus[BASE_KV]);
        return n.format("0.[000]");
      }
      return "";
    }
  },
  validations() {
    let idx = +this.ord - 1;
    let unique = function(value) {
      return !MPCStore.methods
        .getBuses()
        .filter((_, i) => i !== +idx)
        .map(bus => bus[BUS_I])
        .includes(value);
    };
    return {
      bus: {
        [BUS_I]: {
          required,
          integer,
          minValue: minValue(1),
          unique: unique
        },
        [PD]: {
          required,
          decimal
        },
        [QD]: {
          required,
          decimal
        },
        [GS]: {
          required,
          decimal
        },
        [BS]: {
          required,
          decimal
        },
        [VM]: {
          required,
          decimal,
          minValue: minValue(0)
        },
        [VA]: {
          required,
          decimal,
          between: between(-360, 360)
        },
        [BASE_KV]: {
          required,
          decimal,
          minValue: minValue(0)
        },
        [VMAX]: {
          required,
          decimal,
          minValue: minValue(this.bus[VMIN])
        },
        [VMIN]: {
          required,
          decimal,
          maxValue: maxValue(this.bus[VMAX])
        }
      }
    };
  }
};
</script>

<style lang="scss" scoped>
@import "../../main.scss";

fieldset {
  margin-top: 1rem;
  border-top: 0.05rem solid $gray-color;
  padding-top: 0.5rem;
}
</style>
